import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getRoles } from "store/staff"
import { UNASSIGNED_ID } from "../../constants"

const getUnassignedCrewIds = (savedFilters) =>
  savedFilters?.crew_unassigned ? [UNASSIGNED_ID] : []

const getFiltersFromSaved = (savedFilters = {}, activeBookingType = null) => ({
  experience: savedFilters?.experience_ids || [],
  crew: activeBookingType === "my-bookings" ? [] : [
    ...getUnassignedCrewIds(savedFilters),
    ...(savedFilters?.profile_ids || [])
  ],
  equipment: [
    ...(activeBookingType === "all-bookings" && savedFilters?.equipment_unassigned ? [`${UNASSIGNED_ID}_equipment`] : []),
    ...(savedFilters?.boat_ids || [])
  ],
  equipmentFilters: {
    equipment: [
      ...(savedFilters?.equipment_unassigned ? [UNASSIGNED_ID] : []),
      ...(savedFilters?.boat_ids || [])
    ],
    category: savedFilters?.category_ids || []
  }
})

const getCrewFiltersFromSaved = (savedFilters = {}, activeBookingType = null) => ({
  roles: activeBookingType === "my-bookings" ? [] : (savedFilters?.role_ids || []),
  crewMembers: activeBookingType === "my-bookings" ? [] : [
    ...(savedFilters?.crew_unassigned ? [UNASSIGNED_ID] : []),
    ...(savedFilters?.profile_ids || [])
  ]
})

const buildPayload = ({ activeBookingType, crewFilters, filters }) => {
  const isCrew = activeBookingType === "crew"
  const isEquipment = activeBookingType === "equipment"
  const isMyBookings = activeBookingType === "my-bookings"
  const isAllBookings = activeBookingType === "all-bookings"
  
  const crewIds = isCrew ? crewFilters.crewMembers : filters.crew || []
  const crewUnassigned = crewIds.includes(UNASSIGNED_ID)
  const equipmentUnassigned = (filters.equipmentFilters?.equipment || []).includes(UNASSIGNED_ID)
  
  const payload = {}
  
  // All Bookings tab: experience_ids, boat_ids, profile_ids
  if (isAllBookings) {
    const equipmentUnassignedAll = (filters.equipment || []).includes(`${UNASSIGNED_ID}_equipment`)
    payload.experience_ids = filters.experience
    payload.boat_ids = (filters.equipment || []).filter((id) => id !== `${UNASSIGNED_ID}_equipment`)
    payload.profile_ids = crewIds.filter((id) => id !== UNASSIGNED_ID)
    payload.crew_unassigned = crewUnassigned
    payload.equipment_unassigned = equipmentUnassignedAll
    payload.unassigned = crewUnassigned || equipmentUnassignedAll
  }
  
  // My Bookings tab: experience_ids, boat_ids
  if (isMyBookings) {
    payload.experience_ids = filters.experience
    payload.boat_ids = filters.equipment || []
  }
  
  // Crew tab: role_ids, profile_ids
  if (isCrew) {
    payload.crew_unassigned = crewUnassigned
    payload.unassigned = crewUnassigned
    payload.profile_ids = crewIds.filter((id) => id !== UNASSIGNED_ID)
    payload.role_ids = crewFilters.roles
  }
  
  // Equipment tab: boat_ids, category_ids
  if (isEquipment) {
    payload.equipment_unassigned = equipmentUnassigned
    payload.unassigned = equipmentUnassigned
    payload.boat_ids = (filters.equipmentFilters?.equipment || []).filter((id) => id !== UNASSIGNED_ID)
    payload.category_ids = filters.equipmentFilters?.category || []
  }
  
  return payload
}

const useHeaderFilters = ({ savedFilters, activeBookingType, onFiltersChange }) => {
  const dispatch = useDispatch()
  const experiences = useSelector((state) => state.experiences?.list || [])
  const crewProfiles = useSelector((state) => state.staff?.calendarCrew || [])
  const roles = useSelector((state) => state.staff?.roles?.items || [])
  const equipmentList = useSelector((state) => state.calendar?.calendarEquipment || [])
  const [equipmentCategoryOptions, setEquipmentCategoryOptions] = useState([])

  useEffect(() => {
    if (activeBookingType === "crew") dispatch(getRoles())
  }, [activeBookingType, dispatch])

  const filters = useMemo(() => getFiltersFromSaved(savedFilters, activeBookingType), [savedFilters, activeBookingType])
  const crewFilters = useMemo(() => getCrewFiltersFromSaved(savedFilters, activeBookingType), [savedFilters, activeBookingType])

  const setFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(filters) : updater
    onFiltersChange(buildPayload({ activeBookingType, crewFilters, filters: next }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const setCrewFilters = useCallback((updater) => {
    const next = typeof updater === "function" ? updater(crewFilters) : updater
    onFiltersChange(buildPayload({ activeBookingType, crewFilters: next, filters }))
  }, [filters, crewFilters, activeBookingType, onFiltersChange])

  const handleFilterChange = useCallback((key) => (values) => setFilters((prev) => ({ ...prev, [key]: values })), [setFilters])
  const handleFilterRemove = useCallback((key, value) => setFilters((prev) => ({ ...prev, [key]: prev[key].filter((item) => item !== value) })), [setFilters])

  const experienceOptions = useMemo(
    () => (experiences || []).map((exp) => ({ label: exp?.title, value: exp?.id })).filter((o) => o.label && o.value),
    [experiences]
  )
  const crewOptions = useMemo(
    () => (crewProfiles || []).filter((p) => p.status?.toLowerCase() === "active").map((p) => ({ label: p.name, value: p.id })),
    [crewProfiles]
  )
  const crewMemberOptionsWithUnassigned = useMemo(
    () => [{ label: "Unassigned", value: UNASSIGNED_ID }, ...crewOptions],
    [crewOptions]
  )
  const equipmentOptions = useMemo(
    () => (equipmentList || []).map((eq) => ({ label: eq.equipment_title, value: eq.id })).filter((o) => o.label && o.value),
    [equipmentList]
  )
  const equipmentOptionsWithUnassigned = useMemo(
    () => [{ label: "Unassigned Equipment", value: `${UNASSIGNED_ID}_equipment` }, ...equipmentOptions],
    [equipmentOptions]
  )
  const selectedFilters = useMemo(
    () => [
      ...filters.experience.map((v) => ({ type: "experience", value: v, label: experienceOptions.find((o) => o.value === v)?.label ?? v })),
      ...filters.crew.map((v) => ({ type: "crew", value: v, label: v === UNASSIGNED_ID ? "Unassigned" : (crewOptions.find((o) => o.value === v)?.label ?? v) })),
      ...filters.equipment.map((v) => ({ type: "equipment", value: v, label: v === `${UNASSIGNED_ID}_equipment` ? "Unassigned Equipment" : (equipmentOptions.find((o) => o.value === v)?.label ?? v) })),
    ],
    [filters, experienceOptions, crewOptions, equipmentOptions]
  )

  const getOptionLabel = useCallback((value, options = []) => {
    if (value === UNASSIGNED_ID) return "Unassigned"
    return options.find((o) => String(o?.value) === String(value))?.label ?? value
  }, [])

  const removeRole = useCallback((roleId) => setCrewFilters((prev) => ({ ...prev, roles: prev.roles.filter((id) => id !== roleId) })), [setCrewFilters])
  const removeCrewMember = useCallback((memberId) => setCrewFilters((prev) => ({ ...prev, crewMembers: prev.crewMembers.filter((id) => id !== memberId) })), [setCrewFilters])
  const removeEquipment = useCallback((eqId) => setFilters((prev) => ({ ...prev, equipmentFilters: { ...prev.equipmentFilters, equipment: prev.equipmentFilters.equipment.filter((id) => id !== eqId) } })), [setFilters])
  const removeCategory = useCallback((catId) => setFilters((prev) => ({ ...prev, equipmentFilters: { ...prev.equipmentFilters, category: prev.equipmentFilters.category.filter((id) => id !== catId) } })), [setFilters])

  const filterChips = useMemo(() => {
    const isCrew = activeBookingType === "crew"
    const isEquipment = activeBookingType === "equipment"
    if (isCrew) {
      const roleChips = (Array.isArray(roles) ? roles : [])
        .filter((r) => crewFilters.roles.includes(r.id))
        .map((r) => ({ key: `role-${r.id}`, label: r.name, onRemove: () => removeRole(r.id) }))
      const memberChips = crewFilters.crewMembers.map((id) => {
        const member = (Array.isArray(crewProfiles) ? crewProfiles : []).find((m) => m.id === id)
        return { key: `crew-${id}`, label: member ? member.name : id, onRemove: () => removeCrewMember(id) }
      })
      return [...roleChips, ...memberChips]
    }
    if (isEquipment) {
      const eqChips = (filters.equipmentFilters?.equipment || []).map((id) => ({ key: `eq-${id}`, label: getOptionLabel(id, equipmentOptions), onRemove: () => removeEquipment(id) }))
      const catChips = (filters.equipmentFilters?.category || []).map((id) => ({ key: `cat-${id}`, label: getOptionLabel(id, equipmentCategoryOptions), onRemove: () => removeCategory(id) }))
      return [...eqChips, ...catChips]
    }
    return selectedFilters.map((f) => ({ key: `${f.type}-${f.value}`, label: f.label, onRemove: () => handleFilterRemove(f.type, f.value) }))
  }, [activeBookingType, crewFilters, filters, roles, crewProfiles, equipmentOptions, equipmentCategoryOptions, selectedFilters, removeRole, removeCrewMember, removeEquipment, removeCategory, getOptionLabel, handleFilterRemove])

  const crewLoaded = crewProfiles.length > 0
  const equipmentLoaded = equipmentList.length > 0

  return {
    filters, crewFilters, setFilters, setCrewFilters,
    handleFilterChange, handleFilterRemove,
    experienceOptions, crewOptions, crewMemberOptionsWithUnassigned,
    equipmentOptions, equipmentOptionsWithUnassigned, equipmentCategoryOptions, setEquipmentCategoryOptions,
    selectedFilters, filterChips, roles, crewProfiles,
    crewLoaded, equipmentLoaded,
  }
}

export default useHeaderFilters
